import { formatarEndereco, mascararCep, soDigitos } from '@common/utils/endereco';
import { formatarEnderecoExterior } from '@common/utils/enderecoExterior';
import { z } from 'zod';
import type {
  CorpoResposta,
  EnderecoOficialApresentado,
  MinhaParticipacao,
  RespostaSalva,
} from '../types/Formulario';

// Mesmos valores da API (R9 e as 27 UFs); a API valida de novo.
export const TAMANHOS = [
  'PP (XS)',
  'P (S)',
  'M (M)',
  'G (L)',
  'GG (XL)',
  'XG (XL1)',
  '2XG (XL2)',
  '3XG (XL3)',
] as const;

export const UFS = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
] as const;

export { formatarEndereco, mascararCep, soDigitos };

export const enderecoDoCadastro = (o: EnderecoOficialApresentado) =>
  formatarEndereco({
    endereco: o.endereco,
    numero: o.numero,
    complemento: o.complemento,
    bairro: o.distrito,
    cidade: o.cidadeNome,
    uf: o.estadoSigla,
    cep: o.cep,
  });

/** R4 da spec 11: de 8 a 15 dígitos (ignorando espaços, `-`, `.`, `(`, `)`), `+` opcional na frente. */
export const telefoneValido = (v: string) => /^\+?\d{8,15}$/.test(v.replace(/[\s\-.()]/g, ''));

export function enderecoDaEntrega(r: RespostaSalva, cadastro: EnderecoOficialApresentado | null) {
  const e = r.entrega;
  if (!e) return '';
  if (e.origem === 'exterior') return formatarEnderecoExterior(e);
  if (e.origem === 'cadastro' && cadastro && !e.logradouro) return enderecoDoCadastro(cadastro);
  return formatarEndereco({ ...e, endereco: e.logradouro });
}

export type Valores = {
  deseja: '' | 'sim' | 'nao';
  decisao: '' | 'manter' | 'alterar';
  tamanho: string;
  /** `completar` (spec 18) é a entrega "outro" já preenchida com o cadastro; sai como `outro`. */
  origem: '' | 'cadastro' | 'outro' | 'completar' | 'exterior';
  /** Entrega no exterior (spec 11 R3): endereço livre. */
  exterior: {
    /** `pais.id` da lista do Axiis, sem o Brasil (spec 23 R1); `pais` é o nome do item escolhido. */
    paisId: string;
    pais: string;
    endereco: string;
    cidade: string;
    regiao: string;
    codigoPostal: string;
    telefone: string;
  };
  entrega: {
    cep: string;
    logradouro: string;
    numero: string;
    complemento: string;
    bairro: string;
    cidade: string;
    uf: string;
  };
  oficialMarcado: boolean;
  oficial: {
    /** `pais.id` do Axiis; `'1'` é o Brasil (R8). */
    paisId: string;
    cep: string;
    endereco: string;
    numero: string;
    complemento: string;
    distrito: string;
    /** Brasil: ids das listas do Axiis. */
    estadoId: string;
    cidadeId: string;
    /** Fora do Brasil: texto livre (R10). */
    cidade: string;
    regiao: string;
  };
  confirmaEndereco: boolean;
  confirmaRecebedor: boolean;
  /** Spec 33: respostas dos passos "mesmo" e "cadastro"; não vão no `PUT` (o `oficialMarcado` vai). */
  mesmo: '' | 'sim' | 'nao';
  cadastro: '' | 'certo' | 'corrigir' | 'pular';
  /** R11: põe o passo "cadastro" antes de "confirmar" para quem quis o brinde. */
  corrigirCadastro: boolean;
};

const str = (v: string | null | undefined) => v ?? '';
const id = (v: number | null | undefined) => (v == null ? '' : String(v));

/** Valores iniciais: a resposta salva, ou vazio com o endereço oficial da fotografia pré-preenchido (R14). */
export function valoresIniciais(p: MinhaParticipacao): Valores {
  const r = p.resposta;
  const foto = p.dadosApresentados?.enderecoOficial;
  const atual = p.dadosApresentados?.camisetaAtual ?? null;
  const salvoOficial = r?.enderecoOficial.desatualizado ? r.enderecoOficial : null;
  const base = { cidade: '', regiao: '' };
  const oficial: Valores['oficial'] = salvoOficial
    ? (salvoOficial.paisId ?? 1) === 1
      ? {
          ...base,
          paisId: '1',
          cep: mascararCep(salvoOficial.cep),
          endereco: str(salvoOficial.endereco),
          numero: str(salvoOficial.numero),
          complemento: str(salvoOficial.complemento),
          distrito: str(salvoOficial.distrito),
          estadoId: id(salvoOficial.estadoId),
          cidadeId: id(salvoOficial.cidadeId),
        }
      : {
          paisId: id(salvoOficial.paisId),
          cep: str(salvoOficial.cep),
          endereco: str(salvoOficial.endereco),
          numero: str(salvoOficial.numero),
          complemento: str(salvoOficial.complemento),
          distrito: str(salvoOficial.distrito),
          estadoId: '',
          cidadeId: '',
          cidade: str(salvoOficial.cidadeNome),
          regiao: str(salvoOficial.regiao),
        }
    : foto?.exterior
      ? {
          // R8: fora do Brasil os campos livres começam com a fotografia.
          paisId: id(foto.paisId),
          cep: str(foto.cep),
          endereco: str(foto.endereco),
          numero: str(foto.numero),
          complemento: str(foto.complemento),
          distrito: str(foto.distrito),
          estadoId: '',
          cidadeId: '',
          cidade: str(foto.cidadeNome),
          regiao: str(foto.estadoNome),
        }
      : {
          ...base,
          paisId: '1',
          cep: mascararCep(foto?.cep),
          endereco: str(foto?.endereco),
          numero: str(foto?.numero),
          complemento: str(foto?.complemento),
          distrito: str(foto?.distrito),
          estadoId: id(foto?.estadoId),
          cidadeId: id(foto?.cidadeId),
        };
  const e = r?.entrega;
  const marcado = !!salvoOficial;
  const vazio = !temEndereco(foto);
  return {
    deseja: r ? (r.desejaBrinde ? 'sim' : 'nao') : '',
    decisao: atual && r?.tamanho && r.tamanho.decisao !== 'informar' ? r.tamanho.decisao : '',
    tamanho: r?.tamanho && r.tamanho.decisao !== 'manter' ? str(r.tamanho.tamanhoFinal) : '',
    origem: e?.origem ?? '',
    exterior: {
      paisId: e?.origem === 'exterior' ? id(e.paisId) : '',
      pais: e?.origem === 'exterior' ? str(e.pais) : '',
      endereco: e?.origem === 'exterior' ? str(e.endereco) : '',
      cidade: e?.origem === 'exterior' ? str(e.cidade) : '',
      regiao: e?.origem === 'exterior' ? str(e.regiao) : '',
      codigoPostal: e?.origem === 'exterior' ? str(e.codigoPostal) : '',
      telefone: e?.origem === 'exterior' ? str(e.telefone) : '',
    },
    entrega: {
      cep: e?.origem === 'outro' ? mascararCep(e.cep) : '',
      logradouro: e?.origem === 'outro' ? str(e.logradouro) : '',
      numero: e?.origem === 'outro' ? str(e.numero) : '',
      complemento: e?.origem === 'outro' ? str(e.complemento) : '',
      bairro: e?.origem === 'outro' ? str(e.bairro) : '',
      cidade: e?.origem === 'outro' ? str(e.cidade) : '',
      uf: e?.origem === 'outro' ? str(e.uf) : '',
    },
    oficialMarcado: marcado,
    oficial,
    confirmaEndereco: !!r?.confirmacoes?.enderecoCorreto,
    confirmaRecebedor: !!r?.confirmacoes?.recebedorDisponivel,
    // A resposta salva diz o que a pessoa escolheu nos passos (R6, item 2).
    mesmo: e && e.origem !== 'cadastro' ? (marcado ? 'sim' : 'nao') : '',
    // O passo "cadastro" vem depois do "Não" e de "Entregar neste endereço" (R2, R10).
    cadastro: !r
      ? ''
      : r.desejaBrinde && e?.origem !== 'cadastro'
        ? ''
        : marcado
          ? 'corrigir'
          : vazio
            ? 'pular'
            : 'certo',
    corrigirCadastro: false,
  };
}

/** Cadastro sem nenhuma parte do endereço: o passo "cadastro" não tem "Está certo" (R9). */
export const temEndereco = (o: EnderecoOficialApresentado | null | undefined) =>
  !!o && [o.cep, o.endereco, o.numero, o.distrito, o.cidadeNome].some((x) => x?.trim());

export type Passo = 'brinde' | 'tamanho' | 'entrega' | 'mesmo' | 'cadastro' | 'confirmar';

/** Entregas que levam ao passo "mesmo" (R2). */
const ENTREGA_COM_CAMPOS = ['completar', 'outro', 'exterior'];

/**
 * R2: os passos do caminho atual. "Entregar neste endereço" (ou a entrega ainda sem resposta) leva ao
 * passo "cadastro"; as outras entregas, ao "mesmo". Antes da 1ª resposta, o caminho com "Sim" (5 passos).
 */
export function caminho(v: Pick<Valores, 'deseja' | 'origem' | 'corrigirCadastro'>): Passo[] {
  if (v.deseja === 'nao') return ['brinde', 'cadastro', 'confirmar'];
  const passos: Passo[] = ['brinde', 'tamanho', 'entrega'];
  const comCampos = ENTREGA_COM_CAMPOS.includes(v.origem);
  if (comCampos) passos.push('mesmo');
  if (!comCampos || v.corrigirCadastro) passos.push('cadastro');
  passos.push('confirmar');
  return passos;
}

/** O passo já tem resposta (R3: mostra o Continuar; R6: o primeiro sem resposta). */
export function respondido(passo: Passo, v: Valores, atual: string | null): boolean {
  switch (passo) {
    case 'brinde':
      return v.deseja !== '';
    case 'tamanho':
      return atual
        ? v.decisao === 'manter' || (v.decisao === 'alterar' && v.tamanho !== '')
        : v.tamanho !== '';
    case 'entrega':
      return v.origem !== '';
    case 'mesmo':
      return v.mesmo !== '';
    case 'cadastro':
      return v.cadastro !== '';
    case 'confirmar':
      return false;
  }
}

/**
 * R6: sem `?passo=`, o "brinde"; com ele, o passo pedido se o caminho o alcança (todos os anteriores
 * respondidos); senão, o primeiro sem resposta (com tudo respondido, a confirmação).
 */
export function passoAtual(pedido: string | null, v: Valores, atual: string | null): Passo {
  if (!pedido) return 'brinde';
  const passos = caminho(v);
  const primeiro = passos.find((p) => !respondido(p, v, atual)) ?? 'confirmar';
  const i = passos.indexOf(pedido as Passo);
  if (i < 0) return primeiro;
  return passos.slice(0, i).every((p) => respondido(p, v, atual)) ? (pedido as Passo) : primeiro;
}

const OFICIAL: (keyof Valores['oficial'])[] = [
  'paisId',
  'cep',
  'endereco',
  'numero',
  'complemento',
  'distrito',
  'estadoId',
  'cidadeId',
  'cidade',
  'regiao',
];
const ENTREGA: (keyof Valores['entrega'])[] = [
  'cep',
  'logradouro',
  'numero',
  'complemento',
  'bairro',
  'cidade',
  'uf',
];
const EXTERIOR: (keyof Valores['exterior'])[] = [
  'paisId',
  'endereco',
  'cidade',
  'regiao',
  'codigoPostal',
  'telefone',
];

/** R4: os campos que o Continuar valida, pelo caminho (o oficial é do "cadastro" ou da cópia do "mesmo"). */
export function camposDoPasso(passo: Passo, v: Valores): string[] {
  switch (passo) {
    case 'brinde':
      return ['deseja'];
    case 'tamanho':
      return ['decisao', 'tamanho'];
    case 'entrega':
      return [
        'origem',
        ...(v.origem === 'outro' || v.origem === 'completar'
          ? ENTREGA.map((c) => `entrega.${c}`)
          : []),
        ...(v.origem === 'exterior' ? EXTERIOR.map((c) => `exterior.${c}`) : []),
      ];
    case 'mesmo':
      return v.oficialMarcado ? ['oficial.estadoId', 'oficial.cidadeId'] : [];
    case 'cadastro':
      return v.oficialMarcado ? OFICIAL.map((c) => `oficial.${c}`) : [];
    case 'confirmar':
      return ['confirmaEndereco', 'confirmaRecebedor'];
  }
}

/** R14: o passo de um campo com erro. `oficial.*` é sempre do "cadastro" (aberto em "Corrigir"). */
export function passoDoCampo(campo: string): Passo {
  if (campo === 'deseja') return 'brinde';
  if (campo === 'decisao' || campo === 'tamanho') return 'tamanho';
  if (campo === 'origem' || campo.startsWith('entrega.') || campo.startsWith('exterior.'))
    return 'entrega';
  if (campo.startsWith('oficial.')) return 'cadastro';
  return 'confirmar';
}

/** R5: o `oficial` volta aos valores da fotografia quando o passo que o mudou sai do caminho. */
export function oficialDaFotografia(p: MinhaParticipacao): Valores['oficial'] {
  return valoresIniciais({ ...p, resposta: null }).oficial;
}

/** R12: um endereço brasileiro em linhas (rua e número; complemento; bairro; cidade e UF; CEP). */
export function linhasEndereco(e: {
  endereco: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  uf: string | null;
  cep: string | null;
}): string[] {
  const t = (x: string | null) => x?.trim() ?? '';
  return [
    [t(e.endereco), t(e.numero)].filter(Boolean).join(', '),
    t(e.complemento),
    t(e.bairro),
    [t(e.cidade), t(e.uf)].filter(Boolean).join(' – '),
    e.cep && soDigitos(e.cep).length === 8 ? mascararCep(e.cep) : t(e.cep),
  ].filter(Boolean);
}

/** R12: no exterior, endereço livre, cidade, região, código postal, país e telefone. */
export function linhasExterior(e: {
  endereco?: string | null;
  cidade?: string | null;
  regiao?: string | null;
  codigoPostal?: string | null;
  pais?: string | null;
  telefone?: string | null;
}): string[] {
  return [...(e.endereco ?? '').split(/\n/), e.cidade, e.regiao, e.codigoPostal, e.pais, e.telefone]
    .map((x) => x?.trim() ?? '')
    .filter(Boolean);
}

/** O corpo sai da resposta Sim/Não, não do estado bruto do formulário: "não" manda `null` (R7). */
export function montarCorpo(
  v: Valores,
  atual: string | null,
  versaoEsperada: number | null,
): CorpoResposta {
  const quer = v.deseja === 'sim';
  const o = v.oficial;
  const oficial: CorpoResposta['enderecoOficial'] = !v.oficialMarcado
    ? { desatualizado: false }
    : o.paisId === BRASIL
      ? {
          desatualizado: true,
          paisId: 1,
          cep: soDigitos(o.cep),
          endereco: o.endereco.trim(),
          numero: o.numero.trim(),
          complemento: o.complemento.trim() || null,
          distrito: o.distrito.trim(),
          estadoId: Number(o.estadoId),
          cidadeId: Number(o.cidadeId),
        }
      : {
          desatualizado: true,
          paisId: Number(o.paisId),
          cep: o.cep.trim(),
          endereco: o.endereco.trim(),
          numero: o.numero.trim(),
          complemento: o.complemento.trim() || null,
          distrito: o.distrito.trim(),
          cidade: o.cidade.trim(),
          regiao: o.regiao.trim() || null,
        };
  if (!quer) {
    return {
      versaoEsperada,
      desejaBrinde: false,
      tamanho: null,
      entrega: null,
      enderecoOficial: oficial,
      confirmacoes: null,
    };
  }
  const e = v.entrega;
  const x = v.exterior;
  return {
    versaoEsperada,
    desejaBrinde: true,
    tamanho: atual
      ? {
          decisao: v.decisao === 'manter' ? 'manter' : 'alterar',
          tamanhoFinal: v.decisao === 'manter' ? atual : v.tamanho,
        }
      : { decisao: 'informar', tamanhoFinal: v.tamanho },
    entrega:
      v.origem === 'cadastro'
        ? { origem: 'cadastro' }
        : v.origem === 'exterior'
          ? {
              origem: 'exterior',
              paisId: Number(x.paisId),
              pais: x.pais,
              endereco: x.endereco.trim(),
              cidade: x.cidade.trim(),
              regiao: x.regiao.trim() || null,
              codigoPostal: x.codigoPostal.trim() || null,
              telefone: x.telefone.trim(),
            }
          : {
              origem: 'outro',
              cep: soDigitos(e.cep),
              logradouro: e.logradouro.trim(),
              numero: e.numero.trim(),
              complemento: e.complemento.trim() || null,
              bairro: e.bairro.trim(),
              cidade: e.cidade.trim(),
              uf: e.uf,
            },
    enderecoOficial: oficial,
    confirmacoes: { enderecoCorreto: v.confirmaEndereco, recebedorDisponivel: v.confirmaRecebedor },
  };
}

/** Mesmas regras do `validarResposta` da API (R6 a R17); a API continua sendo a fonte. */
export function criarSchema(
  req: string,
  cepMsg: string,
  atual: string | null,
  cadastroOk: boolean,
  msgs: { telefone: string; tamanho: (max: number) => string },
) {
  return z
    .object({
      deseja: z.string(),
      decisao: z.string(),
      tamanho: z.string(),
      origem: z.string(),
      exterior: z.object({
        paisId: z.string(),
        pais: z.string(),
        endereco: z.string(),
        cidade: z.string(),
        regiao: z.string(),
        codigoPostal: z.string(),
        telefone: z.string(),
      }),
      entrega: z.object({
        cep: z.string(),
        logradouro: z.string(),
        numero: z.string(),
        complemento: z.string(),
        bairro: z.string(),
        cidade: z.string(),
        uf: z.string(),
      }),
      oficialMarcado: z.boolean(),
      oficial: z.object({
        paisId: z.string(),
        cep: z.string(),
        endereco: z.string(),
        numero: z.string(),
        complemento: z.string(),
        distrito: z.string(),
        estadoId: z.string(),
        cidadeId: z.string(),
        cidade: z.string(),
        regiao: z.string(),
      }),
      confirmaEndereco: z.boolean(),
      confirmaRecebedor: z.boolean(),
      mesmo: z.string(),
      cadastro: z.string(),
      corrigirCadastro: z.boolean(),
    })
    .superRefine((v, ctx) => {
      const erro = (path: (string | number)[], message = req) =>
        ctx.addIssue({ code: 'custom', path, message });
      const vazio = (s: string) => s.trim() === '';
      const cepRuim = (s: string) => soDigitos(s).length !== 8;
      // Texto livre: obrigatório (quando `obrigatorio`) e com o limite da coluna (R3, R10).
      const texto = (path: string[], s: string, max: number, obrigatorio: boolean) => {
        if (obrigatorio && vazio(s)) erro(path);
        else if (s.trim().length > max) erro(path, msgs.tamanho(max));
      };

      if (v.deseja !== 'sim' && v.deseja !== 'nao') erro(['deseja']);
      if (v.deseja === 'sim') {
        if (atual) {
          if (v.decisao !== 'manter' && v.decisao !== 'alterar') erro(['decisao']);
          if (v.decisao === 'alterar' && (vazio(v.tamanho) || v.tamanho === atual))
            erro(['tamanho']);
        } else if (vazio(v.tamanho)) erro(['tamanho']);

        if (!['cadastro', 'outro', 'completar', 'exterior'].includes(v.origem)) erro(['origem']);
        if (v.origem === 'cadastro' && !cadastroOk) erro(['origem']);
        if (v.origem === 'exterior') {
          const x = v.exterior;
          if (!x.paisId) erro(['exterior', 'paisId']);
          texto(['exterior', 'endereco'], x.endereco, 500, true);
          texto(['exterior', 'cidade'], x.cidade, 255, true);
          texto(['exterior', 'regiao'], x.regiao, 100, false);
          texto(['exterior', 'codigoPostal'], x.codigoPostal, 30, false);
          const tel = x.telefone.trim();
          if (vazio(tel)) erro(['exterior', 'telefone']);
          else if (tel.length > 30 || !telefoneValido(tel))
            erro(['exterior', 'telefone'], msgs.telefone);
        }
        if (v.origem === 'outro' || v.origem === 'completar') {
          const e = v.entrega;
          if (cepRuim(e.cep)) erro(['entrega', 'cep'], cepMsg);
          if (vazio(e.logradouro)) erro(['entrega', 'logradouro']);
          if (vazio(e.numero)) erro(['entrega', 'numero']);
          if (vazio(e.bairro)) erro(['entrega', 'bairro']);
          if (vazio(e.cidade)) erro(['entrega', 'cidade']);
          if (!(UFS as readonly string[]).includes(e.uf)) erro(['entrega', 'uf']);
        }
        if (!v.confirmaEndereco) erro(['confirmaEndereco']);
        if (!v.confirmaRecebedor) erro(['confirmaRecebedor']);
      }
      if (v.oficialMarcado) {
        const o = v.oficial;
        if (!o.paisId) erro(['oficial', 'paisId']);
        else if (o.paisId === BRASIL) {
          if (cepRuim(o.cep)) erro(['oficial', 'cep'], cepMsg);
          if (vazio(o.endereco)) erro(['oficial', 'endereco']);
          if (vazio(o.numero)) erro(['oficial', 'numero']);
          if (vazio(o.distrito)) erro(['oficial', 'distrito']);
          if (!o.estadoId) erro(['oficial', 'estadoId']);
          if (!o.cidadeId) erro(['oficial', 'cidadeId']);
        } else {
          // Fora do Brasil (R10): texto livre, só endereço e cidade são obrigatórios.
          texto(['oficial', 'cep'], o.cep, 30, false);
          texto(['oficial', 'endereco'], o.endereco, 300, true);
          texto(['oficial', 'numero'], o.numero, 10, false);
          texto(['oficial', 'complemento'], o.complemento, 200, false);
          texto(['oficial', 'distrito'], o.distrito, 100, false);
          texto(['oficial', 'cidade'], o.cidade, 255, true);
          texto(['oficial', 'regiao'], o.regiao, 100, false);
        }
      }
    });
}

/** Campo da API (`body.campos`) → caminho no formulário. Chave só com prefixo cai no campo pai. */
export const CAMPO_DA_API: Record<string, string> = {
  desejaBrinde: 'deseja',
  tamanho: 'decisao',
  'tamanho.decisao': 'decisao',
  'tamanho.tamanhoFinal': 'tamanho',
  entrega: 'origem',
  'entrega.origem': 'origem',
  'entrega.cep': 'entrega.cep',
  'entrega.logradouro': 'entrega.logradouro',
  'entrega.numero': 'entrega.numero',
  'entrega.complemento': 'entrega.complemento',
  'entrega.bairro': 'entrega.bairro',
  'entrega.cidade': 'entrega.cidade',
  'entrega.uf': 'entrega.uf',
  confirmacoes: 'confirmaEndereco',
  'confirmacoes.enderecoCorreto': 'confirmaEndereco',
  'confirmacoes.recebedorDisponivel': 'confirmaRecebedor',
  'enderecoOficial.paisId': 'oficial.paisId',
  'enderecoOficial.cidade': 'oficial.cidade',
  'enderecoOficial.regiao': 'oficial.regiao',
  'enderecoOficial.cep': 'oficial.cep',
  'enderecoOficial.endereco': 'oficial.endereco',
  'enderecoOficial.numero': 'oficial.numero',
  'enderecoOficial.complemento': 'oficial.complemento',
  'enderecoOficial.distrito': 'oficial.distrito',
  'enderecoOficial.estadoId': 'oficial.estadoId',
  'enderecoOficial.cidadeId': 'oficial.cidadeId',
};

/** `pais.id` do Brasil no Axiis (R8): só nele valem a busca e a máscara de CEP e as listas de estado e cidade. */
export const BRASIL = '1';

/** Com a entrega no exterior a API devolve `entrega.cidade` etc. para campos de outro bloco (R3). */
export const CAMPO_DA_API_EXTERIOR: Record<string, string> = {
  'entrega.paisId': 'exterior.paisId',
  'entrega.pais': 'exterior.paisId',
  'entrega.endereco': 'exterior.endereco',
  'entrega.cidade': 'exterior.cidade',
  'entrega.regiao': 'exterior.regiao',
  'entrega.codigoPostal': 'exterior.codigoPostal',
  'entrega.telefone': 'exterior.telefone',
};

/** CEP com máscara só para endereço brasileiro; fora do Brasil é texto livre (R8). */
export const mascararCepDoPais = (v: string | null | undefined, brasil = true) =>
  brasil ? mascararCep(v) : (v ?? '');

/** Entrega "outro" com o que o cadastro já tem (spec 18 R6); o formulário só escreve onde está vazio. */
export const entregaDoCadastro = (o: EnderecoOficialApresentado): Valores['entrega'] => ({
  cep: mascararCep(o.cep),
  logradouro: o.endereco ?? '',
  numero: o.numero ?? '',
  complemento: o.complemento ?? '',
  bairro: o.distrito ?? '',
  cidade: o.cidadeNome ?? '',
  uf: o.estadoSigla ?? '',
});

/** A entrega como estava quando foi copiada para o oficial (R8): se mudar, o "mesmo" pergunta de novo. */
export const instantaneoEntrega = (v: Pick<Valores, 'origem' | 'entrega' | 'exterior'>) =>
  JSON.stringify([v.origem, v.entrega, v.exterior]);
